import { useCallback, useMemo, useState } from 'react';

import { useEmployerReactivationBankListModalsStore } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationBankList/stores/useEmployerReactivationBankListModals.store';
import { useEmployerReactivationModalStore } from '@/modules/employer/components/EmployerReactivation/stores';
import useBankInformationQuery from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/useBankInformation.query';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';

export const useEmployerReactivationBankListModals = () => {
  const employerId = useEmployerReactivationModalStore((state) => state.employerId);
  const employerPath = useEmployerReactivationModalStore((state) => state.employerPath);
  
  const viewModalVisible = useEmployerReactivationBankListModalsStore((state) => state.viewModalVisible);
  const editModalVisible = useEmployerReactivationBankListModalsStore((state) => state.editModalVisible);
  const addModalVisible = useEmployerReactivationBankListModalsStore((state) => state.addModalVisible);
  const currentBank = useEmployerReactivationBankListModalsStore((state) => state.currentBank);
  const setViewModalVisible = useEmployerReactivationBankListModalsStore((state) => state.setViewModalVisible);
  const setEditModalVisible = useEmployerReactivationBankListModalsStore((state) => state.setEditModalVisible);
  const setAddModalVisible = useEmployerReactivationBankListModalsStore((state) => state.setAddModalVisible);
  
  const {
    save, isLoading, errorModal, errorModalProps, retry, setErrorModal,
  } = useBankInformationQuery(employerPath, undefined, employerId);

  const [shouldConfirmOnClose, setShouldConfirmOnClose] = useState(false);

  const handleSubmit = async (newValue: BankInfo) => {
    await save({ ...newValue, id: currentBank?.id || '' });
    setEditModalVisible(false);
    setViewModalVisible(false);
  };

  const handleAddSubmit = async (newValue: BankInfo) => {
    await save(newValue);
    setAddModalVisible(false);
  };

  const handleViewCancel = useCallback(() => {
    setViewModalVisible(false);
  }, [setViewModalVisible]);

  const handleEditModalClose = useCallback((value: boolean) => {
    if (!value && shouldConfirmOnClose) {
      return window.confirm('Changes that you made may not be saved.') && setEditModalVisible(value);
    }
    return setEditModalVisible(value);
  }, [shouldConfirmOnClose, setEditModalVisible]);

  const handleEditCancel = useCallback(() => {
    if (shouldConfirmOnClose) {
      return window.confirm('Changes that you made may not be saved.') && setEditModalVisible(false);
    }
    return setEditModalVisible(false);
  }, [shouldConfirmOnClose, setEditModalVisible]);

  const handleAddModalClose = useCallback((value: boolean) => {
    if (!value && shouldConfirmOnClose) {
      return window.confirm('Changes that you made may not be saved.') && setAddModalVisible(value);
    }
    return setAddModalVisible(value);
  }, [shouldConfirmOnClose, setAddModalVisible]);

  const handleAddCancel = useCallback(() => {
    if (shouldConfirmOnClose) {
      return window.confirm('Changes that you made may not be saved.') && setAddModalVisible(false);
    }
    return setAddModalVisible(false);
  }, [shouldConfirmOnClose, setAddModalVisible]);

  const isErrorModalVisible = useMemo(
    () => errorModal && (editModalVisible || viewModalVisible || addModalVisible),
    [errorModal, editModalVisible, viewModalVisible, addModalVisible]
  );

  return {
    viewModalVisible,
    editModalVisible,
    addModalVisible,
    currentBank,
    isLoading,
    errorModal,
    errorModalProps,
    isErrorModalVisible,
    setViewModalVisible,
    setEditModalVisible,
    setAddModalVisible,
    setShouldConfirmOnClose,
    setErrorModal,
    handleSubmit,
    handleAddSubmit,
    handleViewCancel,
    handleEditModalClose,
    handleEditCancel,
    handleAddModalClose,
    handleAddCancel,
    retry,
  };
};
